import {
  keysOverlap,
  type ShortcutBinding,
  type ShortcutId,
  type ShortcutPlatform,
} from "@/lib/shortcut-registry";

/** Claude desktop settings with a global shortcut (native `claude_app_shortcuts`). */
export type ClaudeAppSetting = "quickEntry" | "voice";

/**
 * A Claude profile (native `claude_app_profiles`): the folder Claude was
 * started with (`--user-data-dir`), `null` for the default one, and its
 * name, given only when Claude has more than one profile here.
 */
export type ClaudeProfile = { dir: string | null; name: string | null };

/** A gesture the Claude app listens for, in Denker's key names. */
export type ClaudeAppShortcut = {
  setting: ClaudeAppSetting;
  keys: string[];
  gesture: "chord" | "doubleTap";
  /** The profiles whose settings have it on. */
  profiles: ClaudeProfile[];
};

/**
 * What came of turning a Claude shortcut off in one profile (native
 * `TurnOff`). Denker writes only while no Claude of that profile runs:
 * `claudeRunning` changed nothing; `restarted` means that Claude quit for it
 * and Denker opened it again, as with `quitUnchanged`, where its file then
 * could not change; `alreadyOnce` means Denker did so on its own before and
 * leaves a key the user turned back on.
 */
export type ClaudeTurnOff =
  | "turnedOff"
  | "restarted"
  | "claudeRunning"
  | "quitUnchanged"
  | "alreadyOnce"
  | "unsupported";

/** A Denker shortcut, or the fixed Caps Lock screen recording. */
export type ClashTarget = ShortcutId | "record-screen";

export type ClaudeShortcutClash = {
  target: ClashTarget;
  claude: ClaudeAppShortcut;
  /** Stable while both sides stay the same: a toast shows once per key. */
  key: string;
};

/** Holding Caps Lock records the screen; it is not a configurable shortcut. */
export const RECORD_SCREEN_BINDING: ShortcutBinding = {
  keys: ["CapsLock"],
  gesture: "hold",
};

/**
 * Each setting in Claude's own English labels: Claude names it the same in
 * every Denker language.
 */
const CLAUDE_SETTING_NAME: Record<ClaudeAppSetting, string> = {
  quickEntry: "Quick access shortcut",
  voice: "Voice shortcut",
};
/** Claude on Windows has no native Quick Entry; its older row is named so. */
const WINDOWS_QUICK_ENTRY_NAME = "Quick Entry keyboard shortcut";

export function claudeSettingName(
  setting: ClaudeAppSetting,
  platform: ShortcutPlatform,
): string {
  return platform === "windows" && setting === "quickEntry"
    ? WINDOWS_QUICK_ENTRY_NAME
    : CLAUDE_SETTING_NAME[setting];
}

/** Where the setting sits in the Claude app. */
export function claudeSettingPath(
  setting: ClaudeAppSetting,
  platform: ShortcutPlatform,
): string {
  return `Settings › General › ${claudeSettingName(setting, platform)}`;
}

/**
 * Claude reacts to the same keys: the same gesture, or a press of keys
 * Denker waits on being held (Claude starts on the press).
 */
function clashes(claude: ClaudeAppShortcut, denker: ShortcutBinding): boolean {
  if (denker.holdKeys?.length || !keysOverlap(claude.keys, denker.keys))
    return false;
  return (
    claude.gesture === denker.gesture ||
    (claude.gesture === "chord" && denker.gesture === "hold")
  );
}

/** The same text for the same set of Claude profiles, in any order. */
export function claudeProfilesKey(profiles: ClaudeProfile[]): string {
  return profiles
    .map((profile) => profile.dir ?? "")
    .sort()
    .join("|");
}

/** Every pair of a Claude shortcut and an active Denker binding it shares. */
export function findClaudeShortcutClashes(
  claude: ClaudeAppShortcut[],
  denker: Array<{ target: ClashTarget; binding: ShortcutBinding }>,
): ClaudeShortcutClash[] {
  return claude.flatMap((shortcut) =>
    denker
      .filter(({ binding }) => clashes(shortcut, binding))
      .map(({ target }) => ({
        target,
        claude: shortcut,
        key: [
          target,
          shortcut.setting,
          shortcut.gesture,
          [...shortcut.keys].sort().join("+"),
          claudeProfilesKey(shortcut.profiles),
        ].join(":"),
      })),
  );
}
